import React, { useState, useEffect } from 'react';
import { X, AlertTriangle, User, Loader2, CheckCircle2 } from 'lucide-react';
import { useUpdateNombre } from '../../hooks/useNombres';
import { useChangeRut, useValidateNewRut } from '../../hooks/useChangeRut';

interface ChangePersonalDataModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentRut: string;
  currentNombre: string;
  currentApellido: string;
  personName: string;
  onSuccess?: (changes: { rut?: { old: string; new: string }; nombre?: { old: string; new: string; oldApellido: string; newApellido: string } }) => void;
}

/**
 * Modal unificado para cambiar nombre, apellido y RUT de un personal
 * 
 * ⚠️ OPERACIONES CRÍTICAS:
 * - Cambio de nombre: Afecta base de datos y carpetas de Google Drive
 * - Cambio de RUT: Afecta 7 tablas relacionadas mediante transacción
 */
export const ChangePersonalDataModal: React.FC<ChangePersonalDataModalProps> = ({
  isOpen,
  onClose,
  currentRut,
  currentNombre,
  currentApellido,
  personName,
  onSuccess,
}) => {
  const [nombre, setNombre] = useState('');
  const [apellido, setApellido] = useState('');
  const [newRut, setNewRut] = useState('');
  const [confirmText, setConfirmText] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);
  const [isValidatingRut, setIsValidatingRut] = useState(false);

  const updateNombreMutation = useUpdateNombre();
  const changeRutMutation = useChangeRut();
  const validateRutMutation = useValidateNewRut();

  // Resetear estados al abrir/cerrar el modal
  useEffect(() => {
    if (isOpen) {
      setNombre(currentNombre || '');
      setApellido(currentApellido || '');
      setNewRut(currentRut || '');
      setConfirmText('');
      setValidationError(null);
      setIsValidatingRut(false);
    }
  }, [isOpen, currentNombre, currentApellido, currentRut]);

  // Validar nombre en tiempo real
  useEffect(() => {
    if (!nombre.trim() && !apellido.trim()) {
      setValidationError('Debe proporcionar al menos un nombre o apellido');
      return;
    }

    // Verificar si hay algún cambio
    const nombreCambio = nombre.trim() !== currentNombre || apellido.trim() !== currentApellido;
    const rutCambio = newRut !== currentRut;
    
    if (!nombreCambio && !rutCambio) {
      setValidationError('Debe modificar al menos un campo');
      return;
    }

    setValidationError(null);
  }, [nombre, apellido, currentNombre, currentApellido, newRut, currentRut]);

  // Validar RUT en tiempo real
  useEffect(() => {
    if (!newRut || newRut === currentRut) {
      return;
    }

    // Validación básica de formato
    const rutPattern = /^\d{1,2}\.\d{3}\.\d{3}-[\dkK]$/;
    if (!rutPattern.test(newRut)) {
      setValidationError('Formato de RUT inválido. Use: XX.XXX.XXX-X');
      return;
    }

    // Debounce para no validar en cada tecla
    const timeoutId = setTimeout(async () => {
      setIsValidatingRut(true);
      try {
        const result = await validateRutMutation.mutateAsync(newRut);
        if (!result.available) {
          setValidationError('El RUT ya existe en el sistema');
        } else {
          setValidationError(null);
        }
      } catch (error: any) {
        setValidationError('Error al validar el RUT');
      } finally {
        setIsValidatingRut(false);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [newRut, currentRut]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (confirmText !== 'CONFIRMAR') {
      alert('Debe escribir "CONFIRMAR" para proceder');
      return;
    }

    if (validationError) {
      return;
    }

    if (!nombre.trim() && !apellido.trim()) {
      setValidationError('Debe proporcionar al menos un nombre o apellido');
      return;
    }

    // Determinar qué cambios se harán
    const nombreCambio = nombre.trim() !== currentNombre || apellido.trim() !== currentApellido;
    const rutCambio = newRut !== currentRut;

    if (!nombreCambio && !rutCambio) {
      setValidationError('Debe modificar al menos un campo');
      return;
    }

    try {
      const changes: any = {};

      // Cambiar nombre solo si cambió
      if (nombreCambio) {
        await updateNombreMutation.mutateAsync({
          rut: currentRut,
          nombre: nombre.trim(),
          apellido: apellido.trim(),
        });

        changes.nombre = {
          old: `${currentNombre} ${currentApellido}`,
          new: `${nombre.trim()} ${apellido.trim()}`,
          oldApellido: currentApellido,
          newApellido: apellido.trim(),
        };
      }

      // Cambiar RUT solo si cambió
      if (rutCambio) {
        await changeRutMutation.mutateAsync({
          oldRut: currentRut,
          newRut,
        });

        changes.rut = {
          old: currentRut,
          new: newRut,
        };
      }

      // Llamar callback de éxito si existe
      if (onSuccess) {
        onSuccess(changes);
      }

      // Cerrar modal
      onClose();
    } catch (error: any) {
      // El error ya es manejado por los hooks con toast
      console.error('Error al cambiar datos:', error);
    }
  };

  const canSubmit =
    confirmText === 'CONFIRMAR' &&
    !validationError &&
    !isValidatingRut &&
    !updateNombreMutation.isPending &&
    !changeRutMutation.isPending;

  const isLoading = updateNombreMutation.isPending || changeRutMutation.isPending;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="relative w-full max-w-3xl bg-white rounded-lg shadow-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-orange-600 to-red-600 text-white px-6 py-4 flex justify-between items-center rounded-t-lg z-10">
          <div className="flex items-center gap-3">
            <User className="w-6 h-6" />
            <div>
              <h2 className="text-xl font-bold">Cambiar Datos del Personal</h2>
              <p className="text-orange-100 text-sm mt-1">{personName} - RUT: {currentRut}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={isLoading}
            className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-1 transition-colors disabled:opacity-50"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Advertencias */}
          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded">
            <div className="flex gap-3">
              <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-yellow-800">
                <p className="font-semibold mb-2">⚠️ INFORMACIÓN IMPORTANTE:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>Puede modificar solo nombre, solo apellido, solo RUT, o cualquier combinación</li>
                  <li>Los campos se muestran pre-llenados con los valores actuales</li>
                  <li>El cambio de nombre actualizará la base de datos y carpetas de Google Drive</li>
                  <li>El cambio de RUT afectará 7 tablas relacionadas en una transacción</li>
                  <li>Esta operación es irreversible una vez completada</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Información actual */}
          <div className="bg-gray-50 p-4 rounded-lg space-y-2">
            <h3 className="font-semibold text-gray-700">Datos Actuales</h3>
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div>
                <span className="text-gray-600">Nombre:</span>
                <p className="font-medium">{currentNombre || '(vacío)'}</p>
              </div>
              <div>
                <span className="text-gray-600">Apellido:</span>
                <p className="font-medium">{currentApellido || '(vacío)'}</p>
              </div>
              <div>
                <span className="text-gray-600">RUT:</span>
                <p className="font-medium font-mono text-lg">{currentRut}</p>
              </div>
            </div>
          </div>

          {/* Campos de nombre */}
          <div className="space-y-4">
            <div>
              <label htmlFor="nombre" className="block text-sm font-medium text-gray-700 mb-2">
                Nuevo Nombre *
              </label>
              <input
                type="text"
                id="nombre"
                value={nombre}
                onChange={(e) => setNombre(e.target.value)}
                placeholder="Ingrese el nombre"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:outline-none"
                disabled={isLoading}
                required
              />
            </div>

            <div>
              <label htmlFor="apellido" className="block text-sm font-medium text-gray-700 mb-2">
                Nuevo Apellido *
              </label>
              <input
                type="text"
                id="apellido"
                value={apellido}
                onChange={(e) => setApellido(e.target.value)}
                placeholder="Ingrese el apellido"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:outline-none"
                disabled={isLoading}
                required
              />
            </div>
          </div>

          {/* Campo de RUT */}
          <div>
            <label htmlFor="newRut" className="block text-sm font-medium text-gray-700 mb-2">
              Nuevo RUT *
            </label>
            <div className="relative">
              <input
                type="text"
                id="newRut"
                value={newRut}
                onChange={(e) => setNewRut(e.target.value)}
                placeholder="Ej: 12.345.678-9"
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:outline-none ${
                  validationError && newRut
                    ? 'border-red-300 focus:ring-red-500'
                    : newRut && !isValidatingRut
                    ? 'border-green-300 focus:ring-green-500'
                    : 'border-gray-300 focus:ring-red-500'
                }`}
                disabled={isLoading}
                required
              />
              {isValidatingRut && (
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  <Loader2 className="w-5 h-5 text-blue-500 animate-spin" />
                </div>
              )}
              {newRut && !isValidatingRut && !validationError && (
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  <CheckCircle2 className="w-5 h-5 text-green-500" />
                </div>
              )}
            </div>
            <p className="mt-1 text-xs text-gray-500">Formato: XX.XXX.XXX-X</p>
          </div>

          {/* Error de validación */}
          {validationError && (
            <p className="text-sm text-red-600 bg-red-50 p-3 rounded">{validationError}</p>
          )}

          {/* Confirmación */}
          <div>
            <label htmlFor="confirmText" className="block text-sm font-medium text-gray-700 mb-2">
              Para confirmar, escriba &quot;CONFIRMAR&quot; (en mayúsculas) *
            </label>
            <input
              type="text"
              id="confirmText"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="CONFIRMAR"
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:outline-none ${
                confirmText === 'CONFIRMAR'
                  ? 'border-green-300 focus:ring-green-500'
                  : 'border-gray-300 focus:ring-orange-500'
              }`}
              disabled={isLoading}
              required
            />
            {confirmText && confirmText !== 'CONFIRMAR' && (
              <p className="mt-1 text-sm text-red-600">Debe escribir exactamente &quot;CONFIRMAR&quot;</p>
            )}
          </div>

          {/* Tablas afectadas */}
          <div className="bg-blue-50 p-4 rounded-lg">
            <h3 className="font-semibold text-blue-900 mb-2">Tablas que serán actualizadas (RUT):</h3>
            <div className="grid grid-cols-2 gap-2 text-sm text-blue-800">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                personal_disponible
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                documentos
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                cursos
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                personal_estados
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                personal_clientes
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                programacion_optimizada
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                usuarios
              </div>
            </div>
          </div>

          {/* Botones */}
          <div className="flex gap-4 justify-end pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={!canSubmit}
              className={`px-6 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
                canSubmit
                  ? 'bg-gradient-to-r from-orange-600 to-red-600 text-white hover:from-orange-700 hover:to-red-700'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Aplicando cambios...
                </>
              ) : (
                <>
                  <User className="w-5 h-5" />
                  Aplicar Cambios
                </>
              )}
            </button>
          </div>

          {/* Mensaje de error */}
          {(updateNombreMutation.isError || changeRutMutation.isError) && (
            <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded">
              <p className="text-sm text-red-800">
                <strong>Error:</strong>{' '}
                {(updateNombreMutation.error as any)?.message || 
                 (changeRutMutation.error as any)?.message || 
                 'Error al aplicar los cambios'}
              </p>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};
